import React, { useCallback } from 'react';
import {
  FormControl,
  Select,
  MenuItem,
  Chip,
  Box,
  OutlinedInput,
  SelectChangeEvent,
} from '@mui/material';
import { ConfigurationData, ElementDefinition } from '../../types';
import { usePreselectedValues } from '../../hooks/usePreselectedValues';

interface MultiSelectValueInputProps {
  value: string[];
  onChange: (values: string[]) => void;
  elementName: string;
  config?: ConfigurationData | null;
  elementDefinition?: ElementDefinition | null;
  disabled?: boolean;
  placeholder?: string;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const MultiSelectValueInput: React.FC<MultiSelectValueInputProps> = ({
  value,
  onChange,
  elementName,
  config,
  elementDefinition,
  disabled = false,
  placeholder = 'Values',
}) => {
  const { options: preselectedValues, allowCustom } = usePreselectedValues(elementName, config);

  const handleChange = useCallback(
    (event: SelectChangeEvent<string[]>) => {
      const selectedValues = event.target.value as string[];

      // For numeric types, validate that all values are numeric
      if (elementDefinition?.type === 'numeric') {
        const validValues = selectedValues.filter(val => {
          const numValue = parseFloat(val);
          return !isNaN(numValue) && isFinite(numValue);
        });
        onChange(validValues);
      } else {
        onChange(selectedValues);
      }
    },
    [onChange, elementDefinition]
  );

  const handleDelete = useCallback(
    (valueToDelete: string) => {
      onChange(value.filter(v => v !== valueToDelete));
    },
    [value, onChange]
  );

  // Combine predefined values with any custom values already selected
  const allOptions = React.useMemo(() => {
    const customValues = value.filter(v => !preselectedValues.includes(v));
    return [...preselectedValues, ...customValues];
  }, [preselectedValues, value]);

  return (
    <FormControl fullWidth size="small">
      <Select
        multiple
        value={value}
        onChange={handleChange}
        disabled={disabled}
        input={<OutlinedInput />}
        displayEmpty
        renderValue={selected => {
          if (selected.length === 0) {
            return <em style={{ color: '#999' }}>{placeholder}</em>;
          }

          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map(val => (
                <Chip
                  key={val}
                  label={val}
                  size="small"
                  onDelete={() => handleDelete(val)}
                  onMouseDown={event => {
                    event.stopPropagation();
                  }}
                  sx={{
                    height: 24,
                    fontSize: '12px',
                    '& .MuiChip-deleteIcon': {
                      fontSize: '16px',
                    },
                  }}
                />
              ))}
            </Box>
          );
        }}
        MenuProps={MenuProps}
        sx={{ fontSize: '14px' }}
      >
        <MenuItem disabled value="">
          <em>{placeholder}</em>
        </MenuItem>
        {allOptions.map(option => (
          <MenuItem
            key={option}
            value={option}
            sx={{
              fontWeight: value.includes(option) ? 600 : 400,
            }}
          >
            {option}
            {!preselectedValues.includes(option) && (
              <Chip
                label="custom"
                size="small"
                variant="outlined"
                sx={{ ml: 1, height: 20, fontSize: '10px' }}
              />
            )}
          </MenuItem>
        ))}

        {/* Show hint for custom values if allowed and no predefined options */}
        {allowCustom && allOptions.length === 0 && (
          <MenuItem disabled>
            <em style={{ fontSize: '12px', color: '#666' }}>
              Custom values can be added via text mode
            </em>
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );
};

export default MultiSelectValueInput;
